import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

/**
 * ParticipantAppointmentOutcomesTab — read-only view of COMPLETED appointments.
 *
 * NOTE: This is distinct from:
 *   - Goal Outcomes (Care & Support → Outcomes)
 *   - Participant Outcomes (goal_outcomes collection)
 *   - Clinical Outcomes
 *   - Activity Outcomes
 *
 * Source: /api/appointments?participantId=X&status=COMPLETED (existing endpoint,
 * no new collection). Displays completedAt, appointmentType, and any linked
 * outcome fields already present on the appointment record.
 */
export function ParticipantAppointmentOutcomesTab({ participantId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setDenied(false);
    api.get(`/appointments?participantId=${participantId}&status=COMPLETED&pageSize=50`)
      .then((r) => {
        if (cancelled) return;
        const arr = r?.data?.items || r?.data?.data || [];
        setItems(Array.isArray(arr) ? arr : []);
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403) setDenied(true);
        else setError('Unable to load appointment outcomes for this participant.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  if (denied) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="participant-appointment-outcomes-denied">
        You do not have permission to view appointment outcomes for this participant.
      </div>
    );
  }
  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="participant-appointment-outcomes-loading">Loading appointment outcomes…</div>;
  }
  if (error) {
    return <div className="p-6 text-sm text-red-600" data-testid="participant-appointment-outcomes-error">{error}</div>;
  }
  if (items.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground text-center" data-testid="participant-appointment-outcomes-empty">
        No completed appointments recorded for this participant.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="participant-appointment-outcomes-tab">
      <h3 className="text-sm font-semibold" data-testid="participant-appointment-outcomes-count">
        Completed Appointments ({items.length})
      </h3>
      <p className="text-[11px] text-muted-foreground italic">
        Distinct from Goal / Participant Outcomes.
      </p>
      <div className="space-y-2">
        {items.map((a) => {
          const id = a.id || a._id;
          return (
            <Card key={id} data-testid={`participant-appointment-outcome-row-${id}`}>
              <CardContent className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {a.title || 'Appointment'}
                    <span className="text-xs text-muted-foreground ml-2">{a.appointmentType || '—'}</span>
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    Completed: {a.completedAt || a.scheduledStart || '—'}
                    {a.providerName ? <span className="ml-2">Provider: {a.providerName}</span> : null}
                  </p>
                </div>
                <Badge className="bg-green-100 text-green-800 border-green-200 text-[10px]">COMPLETED</Badge>
                <Button size="sm" variant="ghost" onClick={() => setViewItem(a)} data-testid={`appointment-outcome-view-${id}`}>View</Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Dialog open={!!viewItem} onOpenChange={(o) => { if (!o) setViewItem(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="appointment-outcome-view-dialog">
          <DialogHeader>
            <DialogTitle>{viewItem?.title || 'Appointment'} — Outcome</DialogTitle>
            <DialogDescription>{viewItem?.appointmentType || 'Appointment'} · Completed {viewItem?.completedAt || viewItem?.scheduledStart || '—'}</DialogDescription>
          </DialogHeader>
          {viewItem && (
            <div className="space-y-3 text-sm">
              {viewItem.providerName && <div className="flex justify-between"><span className="text-muted-foreground">Provider</span><span>{viewItem.providerName}</span></div>}
              {viewItem.location && <div className="flex justify-between"><span className="text-muted-foreground">Location</span><span>{viewItem.location}</span></div>}
              <div>
                <p className="text-muted-foreground mb-1">Outcome / Notes</p>
                <p className="whitespace-pre-wrap rounded border p-2 bg-muted/30">{viewItem.outcomeNotes || viewItem.outcome_notes || viewItem.outcome || viewItem.notes || 'No outcome notes recorded.'}</p>
              </div>
              {(viewItem.followUpRequired || viewItem.follow_up_required) && (
                <div className="flex justify-between"><span className="text-muted-foreground">Follow-up</span><span>Required{(viewItem.followUpDate || viewItem.follow_up_date) ? ` by ${viewItem.followUpDate || viewItem.follow_up_date}` : ''}</span></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setViewItem(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ParticipantAppointmentOutcomesTab;
